import { Alert, Anchor, Badge, Center, Loader, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";

import { monoStyle } from "../theme";

interface DecimalProps {
  value: string | number | null | undefined;
  /** Default 2 fraction digits. */
  digits?: number;
  /** Prefix like `$`. */
  prefix?: string;
  /** Treat negative values as loss-coloured. */
  signed?: boolean;
}

export function Decimal({ value, digits = 2, prefix, signed }: DecimalProps) {
  if (value == null || value === "") return <Text component="span" style={monoStyle}>—</Text>;
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return <Text component="span" style={monoStyle}>—</Text>;
  const fixed = n.toFixed(digits);
  const cls = signed ? (n > 0 ? "profit" : n < 0 ? "loss" : undefined) : undefined;
  return (
    <Text
      component="span"
      style={{
        ...monoStyle,
        color: cls === "profit"
          ? "var(--mantine-color-profit-5)"
          : cls === "loss"
          ? "var(--mantine-color-loss-5)"
          : undefined,
      }}
    >
      {prefix ?? ""}{signed && n > 0 ? "+" : ""}{fixed}
    </Text>
  );
}

export function StatusPill({
  state, mode,
}: { state?: string; mode?: string }) {
  const color =
    state === "running" ? "green"
    : state === "degraded" ? "yellow"
    : state === "paused" ? "orange"
    : state === "stopped" ? "gray"
    : "gray";
  const stateLabel = state ? `${state.charAt(0).toUpperCase()}${state.slice(1)}` : "Unknown";
  const modeNorm = (mode ?? "").toString().trim().toLowerCase();
  const modeLabel =
    modeNorm === "paper" || modeNorm === "test" || modeNorm === "dry_run" || modeNorm === "dry-run"
      ? "Test"
      : modeNorm === "live"
      ? "Live"
      : null;
  const label = modeLabel ? `${stateLabel} - ${modeLabel}` : stateLabel;

  return (
    <Badge color={color} radius="sm" size="md">
      {label}
    </Badge>
  );
}

export function FeedHealthPill({ value }: { value?: string | null }) {
  const color = value === "ok" ? "green"
    : value === "stale" ? "yellow"
    : value === "degraded" ? "orange"
    : value === "down" ? "red"
    : "gray";
  return <Badge color={color} variant="light" size="sm">feed: {value ?? "—"}</Badge>;
}

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <Center py="xl">
      <Stack align="center" gap="xs">
        <Loader size="md" />
        <Text c="dimmed" size="sm">{label}</Text>
      </Stack>
    </Center>
  );
}

export function ErrorState({
  error, onRetry,
}: { error: Error | null | undefined; onRetry?: () => void }) {
  return (
    <Alert
      color="red"
      title={error?.name ?? "Error"}
      icon={<IconAlertCircle />}
      variant="light"
    >
      <Stack gap="xs">
        <Text size="sm">{error?.message ?? "unknown error"}</Text>
        {onRetry ? (
          <Anchor
            size="xs"
            c="cyan"
            onClick={onRetry}
          >
            retry →
          </Anchor>
        ) : null}
      </Stack>
    </Alert>
  );
}

export function EmptyState({ label }: { label: string }) {
  return (
    <Center py="xl">
      <Text c="dimmed" size="sm">{label}</Text>
    </Center>
  );
}
